import type { SxProps, Theme } from '@mui/material';
import type {
  NestedTitleBulletListItem,
  StyledTitleBulletListSubtitle,
  TitleBulletListFormattedText,
  TitleBulletListItem,
  TitleBulletListSubtitle,
  TitleBulletListText,
  TitleBulletListTextSegment,
} from './types';

export function isTitleBulletListFormattedText(
  text: TitleBulletListText,
): text is TitleBulletListFormattedText {
  return 'value' in Object(text);
}

export function getTitleBulletListTextSegments(
  value: string,
  boldPhrases: readonly string[],
): TitleBulletListTextSegment[] {
  const segments: TitleBulletListTextSegment[] = [];
  let position = 0;

  while (position < value.length) {
    const match = boldPhrases.reduce<{ index: number; phrase: string } | null>((earliest, phrase) => {
      if (phrase.length === 0) {
        return earliest;
      }
      const index = value.indexOf(phrase, position);
      if (index === -1 || (earliest !== null && index >= earliest.index)) {
        return earliest;
      }
      return { index, phrase };
    }, null);

    if (match === null) {
      segments.push({ text: value.slice(position), bold: false });
      break;
    }

    if (match.index > position) {
      segments.push({ text: value.slice(position, match.index), bold: false });
    }

    segments.push({ text: match.phrase, bold: true });
    position = match.index + match.phrase.length;
  }

  return segments;
}

export function isStyledTitleBulletListSubtitle(
  subtitle: TitleBulletListSubtitle,
): subtitle is StyledTitleBulletListSubtitle {
  return isTitleBulletListFormattedText(subtitle);
}

export function isNestedTitleBulletListItem(
  item: TitleBulletListItem,
): item is NestedTitleBulletListItem {
  return (
    'title' in Object(item) &&
    'items' in Object(item)
  );
}

export function mergeSxProps(base: SxProps<Theme>, sx: SxProps<Theme> | undefined): SxProps<Theme> {
  if (sx == null) {
    return base;
  }

  return [base, ...(Array.isArray(sx) ? sx : [sx])];
}
